/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Spinner } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer, useLocalStore } from 'mobx-react-lite';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { LuisAppStore } from 'src/luis/core/stores/LuisAppStore';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { getBackwardCompatibleRegion } from 'src/verticals/azureMigrationModal/MultiRegionMigrationModal';
import { UserMigrationInfoStore } from 'src/verticals/azureMigrationModal/stores/UserMigrationInfoStore';

export type DeleteUserModalProps = {
    userMigrationInfoStore: UserMigrationInfoStore;
    onDelete: () => void;
    onDismiss: () => void;
};

const messages = defineMessages({
    deleteUserLink: {
        id: 'multiRegionMigrationModal.deleteUserModal.deleteUserLink',
        defaultMessage: 'Delete your account in this region.'
    },
    deleteUserModalTitle: {
        id: 'multiRegionMigrationModal.deleteUserModal.deleteUserModalTitle',
        defaultMessage: 'Delete your account in "{region}" region?'
    },
    deleteUserModalBody: {
        id: 'multiRegionMigrationModal.deleteUserModal.deleteUserModalBody',
        defaultMessage: 'Deleting your account in "{region}" region will delete the following apps:'
    },
    noAppsFound: {
        id: 'multiRegionMigrationModal.deleteUserModal.noAppsFound',
        defaultMessage: 'There are no apps in this region.'
    },
    learnMoreDetailed: {
        id: 'multiRegionMigrationModal.deleteUserModal.learnMoreDetailed',
        defaultMessage: 'Learn more about deleting your account.'
    }
});

/**
 * Represents a modal to user account in certain region.
 */
export const DeleteUserModal = observer((props: DeleteUserModalProps) => {
    const { userMigrationInfoStore, onDelete, onDismiss } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();
    const appsStoreHouse = useLocalStore<ResourceStoreHouse<LuisAppStore>>(createResourceStoreHouse);
    const appNames = appsStoreHouse.items.map(app => app.name);
    const deleteProgressTracker = useProgressTracker({ onComplete: onDelete });

    React.useEffect(() => {
        dispatcher.dispatch('getAppsPerRegion', { userMigrationInfoStore, localStore: appsStoreHouse });
    }, []);

    const handleOnDelete = () => dispatcher.dispatch('deleteUserInRegion', { userMigrationInfoStore, tracker: deleteProgressTracker });
    const region = userMigrationInfoStore?.region ?? getBackwardCompatibleRegion();

    const renderApps = () => {
        if (appNames.length) {
            return (
                <ul>
                    {appNames.map(name => (
                        <li key={name}>{name}</li>
                    ))}
                </ul>
            );
        }

        return <LuisText variant="small">{localize(messages.noAppsFound)}</LuisText>;
    };

    return (
        <DeletePromptModal
            hidden={false}
            onDismiss={onDismiss}
            onDelete={handleOnDelete}
            dialogContentProps={{ title: localize(messages.deleteUserModalTitle, { region }) }}>
            <Stack verticalFill tokens={{ childrenGap: 15 }}>
                <LuisText>{localize(messages.deleteUserModalBody, { region })}</LuisText>

                {appsStoreHouse.isLoading ? <Spinner /> : renderApps()}

                <ExternalLink href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-concept-data-storage#delete-an-account">
                    {localize(messages.learnMoreDetailed)}
                </ExternalLink>
            </Stack>
        </DeletePromptModal>
    );
});
